import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { PaymentTerm } from './models/payment-term.model';
import { MultiPaymentTermsService } from './multi-payment-terms.service';
import { ApiResponse } from '../shared/models/api-response.model';
import { TranslateService } from '@ngx-translate/core';
import { SeFeMenuOptions } from 'se-fe-menu';
import { PaymentTermForm } from './models/payment-term-form.model';
import { PaymentPlanPreview } from '../multi-payment-terms-selector/models/payment-plan-preview.model';
import { SaleGroupItem } from '../multi-payment-terms-selector/models/sale-group-items.model';
import { AmountFormatterService } from 'src/sale-item/amount-formatter.service';
import { Subscription } from 'rxjs';
import moment from 'moment';
import { SeFeThemesLoader } from 'se-fe-themes';
import { SeFeFormFieldConfig } from 'se-fe-form-field';

const PAYMENT_TERMS_MENU_Z_INDEX = '100001';

@Component({
  selector: 'multi-payment-terms-selector-element',
  templateUrl: './multi-payment-terms-selector.component.html',
  styleUrls: ['./multi-payment-terms-selector.component.scss'],
  providers: [{ provide: MultiPaymentTermsService, useClass: MultiPaymentTermsService }],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MultiPaymentTermsSelectorComponent implements OnInit, OnChanges, OnDestroy {
  public upfrontAmountFieldConfig: SeFeFormFieldConfig = {
    label: 'Upfront payment amount',
    errorMessages: {
      required: 'Enter an upfront payment amount',
      min: 'Enter an upfront payment amount',
      max: 'Enter a valid upfront payment amount',
      pattern: 'Enter a valid upfront payment amount',
    },
  };
  paymentTermForm = this.fb.group({});
  initialAmount = '0.00';
  placeholder!: any;
  isLoaded = false;
  center = true;
  isExpanded = false;
  dataOptions: SeFeMenuOptions = {
    name: 'multiMenu',
    sections: [{ menuItems: [] }],
    maxHeight: '200px',
    minWidth: '447px',
  };
  previewPercentages: number[];
  selectedValues = [];
  values = [];
  preSelectedTerm: PaymentTerm[];
  hasCustomDates = false;
  datePrefix = 'customDates-';
  partialAmountInput = false;
  upfrontAmountInputFocused = false;
  applied = false;
  isInvoice: boolean;
  paymentTermsList: PaymentTerm[];
  saleGroupItems: SaleGroupItem[];
  previewData: PaymentPlanPreview;
  private lastSearchQuery: string | null = null;
  @Input() organizationId: string;
  @Input() saleType: string;
  @Input() totalAmount?: string;
  // tslint:disable-next-line:no-input-rename
  @Input('saleGroupItems') items?: string;
  preSelectedCustomDates?: any[];
  preSelectedTermId?: number;
  selectedTerms?: any;
  @Input() newTermsAdded?: boolean | string;
  @Input() preSelectedTerms?: string;
  @Input() triggerPaymentTermError?: string;
  @Input() addPaymentTerms?: boolean;
  @Output() openPaymentTermsModal = new EventEmitter<Boolean>();
  @Output() outputPaymentTermForm = new EventEmitter<any>();
  selectedPaymentTermSubscription: Subscription;
  emitOutputSubscription: Subscription;
  private lastReloadSignature = '';
  private newlyAddedTermId: string | number | null = null;
  private reloadTermsListener = (event: Event) => {
    const e = event as CustomEvent<{ newTermId?: string | number; newTermUuid?: string }>;
    this.newlyAddedTermId = e.detail?.newTermId ?? e.detail?.newTermUuid ?? null;
    this.loadTerms();
  };
  private readonly onPaymentTermsMenuOpen = (event: Event): void => {
    const overlayEvent = event as CustomEvent<{ element?: HTMLElement; type?: string }>;
    const menu = overlayEvent.detail?.element;

    if (overlayEvent.detail?.type === 'popup' && menu?.tagName === 'SE-FE-MENU-TEMPLATE') {
      menu.style.setProperty('--se-z-overlay', PAYMENT_TERMS_MENU_Z_INDEX);
    }
  };

  constructor(
    private fb: FormBuilder,
    private multiPaymentTermsService: MultiPaymentTermsService,
    private translate: TranslateService,
    private amountFormatter: AmountFormatterService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef<HTMLElement>
  ) {
    translate.setDefaultLang('en');
    translate.use('en');
    SeFeThemesLoader.load({
      supportedThemes: 'light',
      url: 'https://se-fe-angular-services.ui.sportngin.com',
    });
  }

  ngOnInit(): void {
    this.selectedPaymentTermSubscription = this.multiPaymentTermsService.selectedPaymentTermSubject.subscribe(
      (paymentTerm: PaymentTerm[]) => {
        this.selectedValues = paymentTerm;
        if (this.preSelectedTerm && this.preSelectedTerm.length > 0) {
          this.preSelectedTerm = this.selectedValues;
          this.reloadOptions();
        }
      }
    );
    this.emitOutputSubscription = this.multiPaymentTermsService.emitOutput.subscribe((emit: boolean) => {
      if (emit) {
        this.emitOutPut();
      }
    });
    this.elementRef.nativeElement.addEventListener('reloadPaymentTerms', this.reloadTermsListener);
    document.addEventListener('seFeOverlay:open', this.onPaymentTermsMenuOpen);
  }

  private isNewTermsAdded(): boolean {
    const v = this.newTermsAdded;
    return v === true || v === 'true';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.newTermsAdded && this.isNewTermsAdded()) {
      this.isInvoice = this.saleType === 'invoice';
      this.initialAmount = '0.00';
      this.selectedTerms = this.preSelectedTerms ? JSON.parse(this.preSelectedTerms) : [];
      this.setSaleGroupItems();
      this.loadTerms();
      this.setTriggerError();
      return;
    }

    if (changes?.triggerPaymentTermError?.currentValue !== 'true') {
      const reloadSignature = JSON.stringify({
        organizationId: this.organizationId || '',
        saleType: this.saleType || '',
        totalAmount: this.totalAmount || '',
        items: this.items || '',
        preSelectedTerms: this.preSelectedTerms || '',
        newTermsAdded: this.isNewTermsAdded(),
      });

      if (reloadSignature === this.lastReloadSignature) {
        this.setTriggerError();
        return;
      }
      this.lastReloadSignature = reloadSignature;

      this.isInvoice = this.saleType === 'invoice';
      this.initialAmount = '0.00';
      this.selectedTerms = this.preSelectedTerms ? JSON.parse(this.preSelectedTerms) : [];
      this.setSaleGroupItems();
      if (this.isInvoice && !this.saleGroupItems && !this.f.showPartialAmountInput) {
        this.paymentTermForm.addControl('showPartialAmountInput', new FormControl(this.partialAmountInput));
      }
      if (this.fullAmountUpfront()) {
        this.resetSelectedValues();
      }
      this.loadTerms();
    }
    this.setTriggerError();
  }

  ngOnDestroy(): void {
    this.elementRef.nativeElement.removeEventListener('reloadPaymentTerms', this.reloadTermsListener);
    document.removeEventListener('seFeOverlay:open', this.onPaymentTermsMenuOpen);
    this.selectedPaymentTermSubscription.unsubscribe();
    this.emitOutputSubscription.unsubscribe();
    if (this.isLoaded) {
      this.multiPaymentTermsService.resetSubjects();
    }
  }

  private setTriggerError(): void {
    let triggerError = false;
    if (this.triggerPaymentTermError === 'true') {
      triggerError = true;
    }
    this.multiPaymentTermsService.updateErrorTriggers({ triggerError: triggerError, emitForm: false });
  }

  loadTerms(): void {
    this.multiPaymentTermsService.getPaymentTerms(this.organizationId).subscribe(
      (terms: ApiResponse<PaymentTerm[]>) => {
        this.paymentTermsList = terms.result
          .filter((term) => !this.isInvoice || term.payment_term_strategy.strategy_type !== 'due_in_full')
          .map((term) => new PaymentTerm(term));
        this.buildDataOptions();
        if (this.lastSearchQuery) {
          this.searchTerms(this.lastSearchQuery);
        }
        if ((this.saleGroupItems || this.totalAmount) && this.selectedValues.length > 0) {
          this.getSelectedTermsPaymentPlans();
        }
        if (this.newlyAddedTermId != null) {
          const term = this.paymentTermsList.find(
            (t) => String(t.id) === String(this.newlyAddedTermId) || String(t.uuid) === String(this.newlyAddedTermId)
          );
          if (term) {
            this.buildAccordionContent(term);
            this.emitOutPut();
          }
          this.newlyAddedTermId = null;
        }
        this.cdr.detectChanges();
      },
      (error) => {
        this.isLoaded = true;
        this.cdr.detectChanges();
      }
    );
  }

  private buildPreSelectedTermAndMenuOptions(): void {
    const preSelect = this.paymentTermsList.filter((term) => this.filterDefaultPaymentTerm(term));
    this.selectedTerms.forEach((preSelectTerm) => {
      this.paymentTermsList.forEach((term: PaymentTerm) => {
        if (this.filterPreselectedTerms(term, preSelectTerm)) {
          preSelect.push(term);
        }
      });
    });
    this.preSelectedTerm = preSelect.map((x) => x);
    this.reloadOptions();
    this.isLoaded = true;
  }

  private buildAccordionContentForPreSelected(): void {
    this.preSelectedTerm.forEach((term: PaymentTerm) => {
      this.preSelectedTermId = term.id;
      if (
        this.selectedTerms &&
        this.selectedTerms.length > 0 &&
        term.payment_term_strategy.strategy_type !== 'due_in_full'
      ) {
        const dates = this.selectedTerms.filter(
          (preSelectedTerm) =>
            preSelectedTerm.payment_term_id === term.id || preSelectedTerm.payment_term_uuid === term.uuid
        );
        if (dates && dates.length > 0) {
          this.preSelectedCustomDates = dates[0].payment_term_dates;
        }
      }
      this.buildAccordionContent(term);
    });
  }

  buildDataOptions(): void {
    this.buildPreSelectedTermAndMenuOptions();
    this.buildAccordionContentForPreSelected();
    this.cdr.detectChanges();
  }

  private filterPreselectedTerms(term: PaymentTerm, preSelectTerm: any): boolean {
    const matchingTerm =
      String(term.id) === String(preSelectTerm.payment_term_id) ||
      String(term.uuid) === String(preSelectTerm.payment_term_uuid);

    return matchingTerm && !this.filterDefaultPaymentTerm(term);
  }

  buildAccordionContent(paymentTerm: PaymentTerm): void {
    const exists = this.selectedValues.filter((item) => item.id === paymentTerm.id).length > 0 ? true : false;
    if (this.selectedValues.length < 4) {
      const term = this.paymentTermsList.filter((item) => item.id === paymentTerm.id)[0];
      if (term) {
        if (!exists) {
          this.selectedValues.push(term);
          if (this.isInvoice) {
            const hasItemsOrTotal = !!(this.saleGroupItems?.length || this.totalAmount);
            if (hasItemsOrTotal) {
              this.getPaymentPlans(term);
            } else {
              this.multiPaymentTermsService.selectedPaymentTermSubject.next(this.selectedValues);
              this.emitOutPut();
            }
          } else {
            this.multiPaymentTermsService.selectedPaymentTermSubject.next(this.selectedValues);
          }
        } else {
          this.multiPaymentTermsService.selectedPaymentTermSubject.next(this.selectedValues);
        }
        const installmentCount = Number(term.payment_term_strategy.installment_count) || 0;
        this.hasCustomDates = term.payment_term_strategy.strategy_type === 'custom_date';
        const customDates = this.customDatesControl(String(term.id));
        this.previewPercentages = term.payment_term_strategy.preview_percentages;
        if (this.hasCustomDates && !customDates) {
          this.paymentTermForm.addControl(this.datePrefix + String(term.id), this.fb.array([]));
          this.addCustomDates(installmentCount, String(term.id));
        } else if (this.hasCustomDates && customDates.length !== installmentCount) {
          this.customDatesControl(String(term.id)).clear();
          this.addCustomDates(installmentCount, this.datePrefix + String(term.id));
        } else if (!this.hasCustomDates && customDates) {
        }
      }
    }
    this.emitOutPut();
  }

  private addCustomDates(count: number, id: string): void {
    const preSelectedDatesExists = String(this.preSelectedTermId) === id;
    let localTimer;
    for (let i = 0; i < count; i++) {
      this.customDatesControl(id).push(this.createInstallmentDate(i, preSelectedDatesExists, id));
      this.customDatesControl(id).controls.map((control) => {
        control.valueChanges.subscribe((value) => {
          if (localTimer) {
            clearTimeout(localTimer);
          }
          localTimer = setTimeout(() => {
            this.emitOutPut();
          });
        });
      });
    }
    this.multiPaymentTermsService.customDatesControl.next(id);
  }

  fullAmountUpfront(): boolean {
    if (!this.saleGroupItems || !this.saleGroupItems.length) {
      return false;
    }

    return this.saleGroupItems.reduce((acc, cur) => acc + (cur.price_cents - cur.upfront_amount_cents), 0) < 1;
  }

  resetSelectedValues(): void {
    this.selectedValues = this.selectedValues.filter((term) => this.filterDefaultPaymentTerm(term));
    this.paymentTermForm = this.fb.group({});
    this.multiPaymentTermsService.selectedPaymentTermSubject.next(this.selectedValues);
    if (this.selectedValues[0]) {
      this.buildAccordionContent(this.selectedValues[0]);
    }
  }

  createInstallmentDate(index: number, preSelectedDatesExists: boolean, id: string): FormGroup {
    let addCustomDates;
    if (preSelectedDatesExists && this.preSelectedCustomDates && this.preSelectedCustomDates.length > 0) {
      addCustomDates = this.preSelectedCustomDates[index];
    }
    return this.fb.group({
      installment: [
        addCustomDates ? addCustomDates : '',
        [Validators.required], // TODO: fix -> , this.pastDateValidatorFn.bind(this), this.compareDateValidatorFn.bind(this, id)]
      ],
    });
  }

  pastDateValidatorFn(control: AbstractControl): ValidationErrors | null {
    if (control?.value) {
      if (this.pastDateToToday(control.value)) {
        return { pastDate: true };
      }
    }
    return null;
  }

  compareDateValidatorFn(id: string, control: AbstractControl): ValidationErrors | null {
    if (control?.value) {
      setTimeout(() => {
        this.setValidationErrors(id);
      }, 100);
    }
    return null;
  }

  pastDateToToday(value: string): boolean {
    const today = moment().startOf('day').valueOf();
    const dateToCheck = moment(new Date(value)).valueOf();
    if (dateToCheck < today) {
      return true;
    }
    return false;
  }

  futureDate(date1: string, date2?: string): boolean {
    const momentDate1 = moment(new Date(date1)).valueOf();
    const momentDate2 = moment(new Date(date2)).valueOf();
    if (momentDate1 < momentDate2) {
      return true;
    }
    return false;
  }

  setValidationErrors(id: string): void {
    const customDatesControl = this.customDatesControl(id);
    if (!customDatesControl) {
      return;
    }

    let previousDate, previousControl, currentDate, currentControl, nextDate, nextControl;
    for (let x = 0; x < customDatesControl.controls.length; x++) {
      currentControl = customDatesControl.controls[x];
      nextControl = customDatesControl.controls[x + 1];
      currentDate = currentControl.value.installment;
      nextDate = nextControl ? nextControl.value.installment : null;
      currentControl.controls.installment.setErrors({ compareDate: null });
      currentControl.controls.installment.setErrors({ priorDate: null });
      currentControl.controls.installment.setErrors({ nextDate: null });
      currentControl.controls.installment.updateValueAndValidity({ emitEvent: false });

      if (this.pastDateToToday(currentDate)) {
        currentControl.controls.installment.setErrors({ pastDate: true });
        continue;
      }
      if (x > 0) {
        previousControl = customDatesControl.controls[x - 1];
        previousDate = previousControl.value.installment;
      }
      if (x === 0) {
        if (currentDate && nextDate) {
          if (currentDate === nextDate) {
            nextControl.controls.installment.setErrors({ compareDate: true });
            continue;
          }
          if (!this.futureDate(currentDate, nextDate) && !this.pastDateToToday(nextDate)) {
            nextControl.controls.installment.setErrors({ priorDate: true });
            continue;
          }
        } else if (!currentDate && currentControl) {
          currentControl.controls.installment.setErrors({ required: true });
          continue;
        } else if (!nextDate && nextControl) {
          nextControl.controls.installment.setErrors({ required: true });
          continue;
        }
      }
      if (x === customDatesControl.controls.length - 1) {
        if (currentDate && previousDate) {
          if (currentDate === previousDate) {
            currentControl.controls.installment.setErrors({ compareDate: true });
            break;
          }
          if (this.futureDate(currentDate, previousDate) && !this.pastDateToToday(previousDate)) {
            currentControl.controls.installment.setErrors({ nextDate: true });
            break;
          }
        } else if (!currentDate && currentControl) {
          currentControl.controls.installment.setErrors({ required: true });
          continue;
        } else if (!previousDate && previousControl) {
          previousControl.controls.installment.setErrors({ required: true });
          continue;
        }
      }
      if (currentDate && previousDate) {
        if (currentDate === previousDate) {
          currentControl.controls.installment.setErrors({ compareDate: true });
          continue;
        }
        if (this.futureDate(currentDate, previousDate) && !this.pastDateToToday(previousDate)) {
          currentControl.controls.installment.setErrors({ nextDate: true });
          continue;
        }
      }
    }
  }

  private setDataOptionsMenuItems(menuItems: { text: string; action: () => void }[]): void {
    this.dataOptions = {
      ...this.dataOptions,
      sections: [{ ...this.dataOptions.sections[0], menuItems }],
    };
  }

  searchTerms(event: any): void {
    this.lastSearchQuery = event || null;
    if (event) {
      const query = event.toLowerCase();
      const menuItems: { text: string; action: () => void }[] = [];
      this.paymentTermsList.forEach((term: PaymentTerm) => {
        const preSelectedTerm =
          this.preSelectedTerm ?
            this.preSelectedTerm.filter((paymentTerm: PaymentTerm) => paymentTerm.id === term.id)
          : undefined;
        if (preSelectedTerm && preSelectedTerm.length > 0) {
          return;
        }
        if (term.name.toLowerCase().includes(query)) {
          menuItems.push({
            text: term.name,
            action: () => {
              this.buildAccordionContent(term);
              this.cdr.detectChanges();
            },
          });
        }
      });
      this.setDataOptionsMenuItems(menuItems);
    } else {
      this.reloadOptions();
    }
  }

  reloadOptions(): void {
    const menuItems: { text: string; action: () => void }[] = [];
    this.paymentTermsList.forEach((term: PaymentTerm) => {
      const preSelectedTerm =
        this.preSelectedTerm ?
          this.preSelectedTerm.filter((paymentTerm: PaymentTerm) => paymentTerm.id === term.id)
        : undefined;
      if (preSelectedTerm && preSelectedTerm.length > 0) {
        return;
      }
      menuItems.push({
        text: term.name,
        action: () => {
          this.buildAccordionContent(term);
          this.cdr.detectChanges();
        },
      });
    });
    this.setDataOptionsMenuItems(menuItems);
  }

  toggleUpfrontPayment(): void {
    this.partialAmountInput = !this.partialAmountInput;
    if (this.partialAmountInput && !this.f.upfrontAmount) {
      const amount = this.initialAmount && this.initialAmount !== '0.00' ? Number(this.initialAmount) : '';
      this.paymentTermForm.addControl(
        'upfrontAmount',
        new FormControl(amount, [
          Validators.pattern(/^(\$)?(([0-9,]+)?([0-9]\.)?(\.[0-9]+)?)$/),
          this.upfrontAmountValidator(),
        ])
      );
    } else {
      this.applied = false;
      this.paymentTermForm.removeControl('upfrontAmount');
      this.initialAmount = '0.00';
      this.getSelectedTermsPaymentPlans();
    }
  }

  applyUpfrontAmount(event?: MouseEvent): void {
    this.f.upfrontAmount.markAsDirty();
    if (event) {
      event.preventDefault();
    }
    if (this.upfrontAmountInputFocused) {
      this.setStatusAndFormatAmount();
    }
    if (this.f.upfrontAmount.invalid) {
      return;
    }
    this.applied = true;
    this.sendAmountAndSetStatus();
  }

  getSelectedTermsPaymentPlans(): void {
    this.selectedValues.forEach((term: PaymentTerm) => {
      this.getPaymentPlans(term);
    });
  }

  getPaymentPlans(term: PaymentTerm): void {
    const hasEmptyPayments = term.previewData?.payments?.length === 0;
    const hasItemsOrTotal = !!(this.saleGroupItems?.length || this.totalAmount);
    const shouldRefetch = hasEmptyPayments && hasItemsOrTotal;
    if (term.previewData && !shouldRefetch) {
      this.multiPaymentTermsService.selectedPaymentTermSubject.next(this.selectedValues);
      this.emitOutPut();
      return;
    }
    if (shouldRefetch) {
      term.previewData = null;
    }
    this.isLoaded = true;
    const sanitizedInitialAmount = this.amountFormatter.sanitizePrice(this.initialAmount);
    this.multiPaymentTermsService
      .getPlanPreview(term.id, this.saleGroupItems, Number(sanitizedInitialAmount), this.totalAmount)
      .subscribe(
        (preview: ApiResponse<PaymentPlanPreview>) => {
          this.previewData = preview.result;
          this.multiPaymentTermsService.previewData.next(preview.result);
          term.previewData = this.previewData;
          const index = this.selectedValues.findIndex((value) => value.id === term.id);
          if (this.selectedValues.length > 0 && index !== -1) {
            this.selectedValues[index].previewData = term.previewData;
          } else {
            this.selectedValues.push(term);
          }
          this.isLoaded = true;
          this.multiPaymentTermsService.selectedPaymentTermSubject.next(this.selectedValues);
          if (this.f?.showPartialAmountInput) {
            this.f.showPartialAmountInput.setValue(this.partialAmountInput);
            this.f.showPartialAmountInput.updateValueAndValidity();
          }
          this.emitOutPut();
        },
        (error) => {
          this.isLoaded = true;
          console.error('error', error);
        }
      );
  }

  private upfrontAmountValidator(): ValidatorFn {
    return (formControl: FormControl) => {
      if (!formControl.value) {
        return { required: true };
      } else if (this.multiPaymentTermsService.sanitizedAmount(formControl.value) <= 0) {
        return { min: true };
      } else if (this.multiPaymentTermsService.sanitizedAmount(formControl.value) > this.maxUpfrontPayment()) {
        return { max: true };
      }
      return null;
    };
  }

  setSaleGroupItems(): void {
    try {
      this.saleGroupItems = this.items ? JSON.parse(this.items) : null;
    } catch (ex) {
      this.saleGroupItems = null;
      console.error('Error parsing sale group items', this.items, ex);
    }
  }

  sendAmountAndSetStatus(): void {
    this.initialAmount = this.f.upfrontAmount.value;
    this.multiPaymentTermsService.upfrontPaymentAmountSubject.next(this.f.upfrontAmount.value);
    this.selectedValues.forEach((term: PaymentTerm) => {
      term.previewData = null;
    });
    this.getSelectedTermsPaymentPlans();
  }

  setUpfrontAmountFocus(): void {
    this.upfrontAmountInputFocused = true;
  }

  setStatusAndFormatAmount(): void {
    this.upfrontAmountInputFocused = false;
    this.formatAmount();
  }

  formatAmount(): number {
    let amount = this.f.upfrontAmount.value;

    if (!amount) {
      return;
    }
    amount = this.multiPaymentTermsService.sanitizedAmount(amount);
    if (isNaN(amount)) {
      return;
    }
    this.f.upfrontAmount.setValue(this.amountFormatter.formattedAmount(amount));
    return amount;
  }

  maxUpfrontPayment(): number {
    if (!this.previewData) {
      return;
    }
    const total = this.multiPaymentTermsService.sanitizedAmount(this.totalAmount);
    if (Number(this.previewData.initial_payment_amount) > 0) {
      return total - (this.previewData.payments.length - 1) / 100;
    } else {
      return total - this.previewData.payments.length / 100;
    }
  }

  customDatesControl(id: string): FormArray {
    return this.paymentTermForm.get(this.datePrefix + id) as FormArray;
  }

  get f(): { [key: string]: AbstractControl } {
    return this.paymentTermForm.controls;
  }

  paymentTerms(): any {
    return !this.isInvoice && this.preSelectedTerm ?
        this.selectedValues.concat(this.preSelectedTerm)
      : this.selectedValues;
  }

  emitOutPut(): void {
    const output = [];
    const finalSelectedTerms = this.selectedValues;
    if (finalSelectedTerms.length > 0) {
      finalSelectedTerms.forEach((term) => {
        output.push(new PaymentTermForm(this.paymentTermForm, term, term.previewData, this.applied));
      });
    }
    this.outputPaymentTermForm.emit(output);
  }

  openModal(): void {
    this.openPaymentTermsModal.emit(true);
  }

  private filterDefaultPaymentTerm(paymentTerm: PaymentTerm): boolean {
    if (this.isInvoice) {
      return paymentTerm.name === 'Due on Custom Date' && paymentTerm.default;
    }
    return paymentTerm.payment_term_strategy.strategy_type === 'due_in_full' && paymentTerm.default;
  }
}
